import { defineStore } from "pinia"
import { computed, ref, watch, type Ref } from "vue"
import { LayoutColumn } from "@/models/user_settings/layout-column.model"
import { useAuthStore } from "@/store/auth.store"
import { LayoutColumnComponents } from "@/enums/models/layout-column-components.enum"
import { v4 as uuidv4 } from "uuid"

export const useLayoutColumnsStore = defineStore("layoutColumnsStore", () => {
  const authStore = useAuthStore()
  const layoutColumns: Ref<LayoutColumn[]> = ref([])
  const activeColumnComponent: Ref<LayoutColumnComponents | null> = ref(null)

  const showLayoutControls = ref(false)
  const initialLayoutColumnsCount: Ref<number | null> = ref(null)
  const columnsLoadingCompletedCount = ref(0)
  const isAnyLayoutControlHovered = ref(false)

  const instructionColumns = computed(() => {
    return layoutColumns.value.filter(
      (column: LayoutColumn) => column.component === LayoutColumnComponents.INSTRUCTION_LIST
    )
  })

  const fileViewers = computed(() => {
    return layoutColumns.value.filter(
      (column: LayoutColumn) => column.component === LayoutColumnComponents.FILE_VIEWER
    )
  })

  const allColumnsLoaded = computed(() => {
    return (
      initialLayoutColumnsCount.value === null ||
      (initialLayoutColumnsCount.value !== null &&
        columnsLoadingCompletedCount.value >= initialLayoutColumnsCount.value)
    )
  })

  /**
   * Sets the active column component (app, map, etc.)
   * @param id - The enum of the column component to set as active
   */
  function setActiveColumnComponent(component: LayoutColumnComponents) {
    activeColumnComponent.value = component
    layoutColumns.value.forEach((column) => {
      column.active = column.component === component
    })
  }

  function hasColumn(type: LayoutColumnComponents) {
    return layoutColumns.value.some((column) => column.component === type)
  }

  function addColumn(layoutColumn: LayoutColumn, atIndex?: number) {
    if (!LayoutColumn.canAccess(layoutColumn.component, authStore.activeUser)) return
    layoutColumn.id = uuidv4()

    if (atIndex !== undefined) {
      layoutColumns.value.splice(atIndex, 0, layoutColumn)
    } else {
      layoutColumns.value.push(layoutColumn)
    }
  }

  function removeColumn(columnId: string) {
    const index = layoutColumns.value.findIndex((column) => column.id === columnId)
    if (index !== -1) {
      layoutColumns.value.splice(index, 1)
    }
  }

  function onColumnLoadingCompleted() {
    columnsLoadingCompletedCount.value++
  }

  function setLayoutControlHovered(hovered: boolean) {
    isAnyLayoutControlHovered.value = hovered
  }

  watch(layoutColumns, (val) => {
    if (!initialLayoutColumnsCount.value) {
      initialLayoutColumnsCount.value = val.length
    }
  })

  return {
    layoutColumns,
    instructionColumns,
    fileViewers,
    showLayoutControls,
    allColumnsLoaded,
    activeColumnComponent,
    isAnyLayoutControlHovered,
    addColumn,
    hasColumn,
    removeColumn,
    onColumnLoadingCompleted,
    setActiveColumnComponent,
    setLayoutControlHovered
  }
})
